import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { apiGet } from "../api";
import { useAuth } from "../auth/AuthContext";
import type { PublicHealthStatus } from "../types";
import { Skeleton } from "./Loaders";
import { Icon } from "./Icons";

type Status = "loading" | "up" | "down";

function formatCardDataUpdatedAt(value?: string | null): string {
  if (!value) return "Card data: synced";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "Card data: synced";
  return `Card data: updated ${d.toLocaleString(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  })}`;
}

export function Footer() {
  const { firebaseUser, profile } = useAuth();
  const [health, setHealth] = useState<PublicHealthStatus | null>(null);
  const [status, setStatus] = useState<Status>("loading");

  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      try {
        const data = await apiGet<PublicHealthStatus>("/api/health");
        if (!cancelled) {
          setHealth(data);
          setStatus(data?.ollama_reachable ? "up" : "down");
        }
      } catch {
        if (!cancelled) {
          setHealth(null);
          setStatus("down");
        }
      }
    };
    tick();
    const id = setInterval(tick, 30000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, []);

  return (
    <footer className="site-footer">
      <div className="footer-brand-row">
        <img src="/foilbloom-icon-text.png" alt="Foil Bloom" />
        <span>AI deck OS for brewers who like their mana bases legal and their UI shiny.</span>
      </div>

      <div className="footer-row footer-links">
        <Link to="/">Home</Link>
        {firebaseUser ? (
          <>
            <Link to="/build">Build</Link>
            <Link to="/collection">My Collection</Link>
            <Link to="/analyze">Analyze</Link>
            <Link to="/decks">Decks</Link>
            <Link to="/community">Community</Link>
            <Link to="/account">Account</Link>
            {profile?.isAdmin && <Link to="/admin">Admin</Link>}
          </>
        ) : (
          <>
            <Link to="/community">Community</Link>
            <Link to="/login">Sign in</Link>
            <Link to="/register">Sign up</Link>
          </>
        )}
        <span className="footer-sep" aria-hidden>.</span>
        <Link to="/downloads">Downloads</Link>
        <Link to="/pricing">Pricing</Link>
        <Link to="/about">About</Link>
      </div>

      <div className="footer-row footer-meta">
        <span className="footer-beta-note" title="Foil Bloom is in active beta. AI builds may be a little slow.">
          <strong>Beta</strong>
          AI builds may be slow
        </span>
        <span className="footer-sep" aria-hidden>.</span>
        {status === "loading" ? (
          <Skeleton variant="text" width={240} />
        ) : (
          <>
            <span className={`status-dot${status === "down" ? " down" : ""}`}>
              <span className="dot" />
              {status === "up" ? "All systems operational" : "LLM unreachable"}
            </span>
            <span className="footer-sep" aria-hidden>.</span>
            <span>
              <Icon name="cloud" size={14} /> {formatCardDataUpdatedAt(health?.card_data_updated_at)}
            </span>
          </>
        )}
      </div>
    </footer>
  );
}
